/**
 * Página: AlertasDashboard — Centro de Alertas Kloud OPS
 * Dashboard operativo: salud de proveedores cloud (mapa) + alertas de clientes (tabla)
 * Paleta: Kloud OPS (violeta + acentos cyan)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchNotificationAlert } from '../../api';
import {
  RefreshCw,
  Download,
  Clock,
  Globe2,
  Users,
  Search,
  X,
  Filter
} from 'lucide-react';
import alertsApi from '../../services/alertsApi';
import AlertsMap from '../../components/AlertsMap';
import ClientAlertsTable from '../../components/ClientAlertsTable';
import AlertDetailModal from '../../components/AlertDetailModal';

// Semáforo visual (mismo criterio que el chat de operaciones)
const SEVERITY_COLORS = {
  CRITICAL: '#ef4444', // 🔴 P1: escalar inmediato
  HIGH: '#f97316',     // 🟠 P2: atender prioritario
  MEDIUM: '#facc15',   // 🟡 P3: revisar impacto
  LOW: '#4ade80',      // 🟢 P4: monitorear
  INFO: '#60a5fa'      // 🔵 P5: solo seguimiento
};

const PROVIDER_COLORS = {
  AWS: '#fb923c',
  Azure: '#38bdf8',
  GCP: '#34d399',
  Huawei: '#f87171',
  M365: '#f472b6'
};

// Mes por defecto = mes actual en formato YYYY-MM
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Convierte "YYYY-MM" al primer y último día del mes en formato YYYY-MM-DD
const monthToDateRange = (ym) => {
  const [year, month] = ym.split('-').map(Number);
  const last = new Date(year, month, 0).getDate();
  return {
    inicio: `${ym}-01`,
    fin: `${ym}-${String(last).padStart(2, '0')}`
  };
};

export default function AlertasDashboard() {
  // Estados de datos
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [providerAlerts, setProviderAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alertsByRegion, setAlertsByRegion] = useState({});
  const [alertsByProvider, setAlertsByProvider] = useState({});
  const [clientAlerts, setClientAlerts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [connectionError, setConnectionError] = useState(false);

  // Filtros globales (van al backend → re-fetch)
  const [mesFilter, setMesFilter] = useState(getCurrentMonth);
  const [clienteFilter, setClienteFilter] = useState('');

  // Filtros locales (client-side sobre la respuesta)
  const [cloudFilter, setCloudFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  // Filtro por región del mapa (alertas de proveedor)
  const [regionFilter, setRegionFilter] = useState(null);
  // Filtro por proveedor (del mapa o de la lista lateral)
  const [providerFilter, setProviderFilter] = useState(null);

  const handleProviderClick = (provider) => {
    setProviderFilter((prev) => (prev === provider ? null : provider));
  };

  // Alerta seleccionada para el modal de detalle
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [notificationError, setNotificationError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationFingerprint = searchParams.get('alert');

  useEffect(() => {
    if (!notificationFingerprint) return;
    const controller = new AbortController();
    setSelectedAlert(null);
    setNotificationError('');
    fetchNotificationAlert(notificationFingerprint, controller.signal).then((alert) => {
      if (!controller.signal.aborted) setSelectedAlert({ ...alert, nube: alert.nube || alert.Nube || alert.provider, cliente: alert.cliente || alert.customer_name });
    }).catch((error) => {
      if (!controller.signal.aborted) setNotificationError(error.response?.status === 404 ? 'La alerta ya no esta disponible.' : 'No se pudo abrir la alerta. Verifica tu acceso e intenta de nuevo.');
    });
    return () => controller.abort();
  }, [notificationFingerprint]);

  const closeNotificationAlert = () => {
    setSelectedAlert(null);
    const params = new URLSearchParams(searchParams);
    params.delete('alert');
    setSearchParams(params, { replace: true });
  };

  // Lista de clientes únicos (se puebla desde la data)
  const [availableClientes, setAvailableClientes] = useState([]);

  // Re-fetch cuando cambian filtros globales
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesFilter, clienteFilter]);

  const fetchAllData = useCallback(async () => {
    try {
      setRefreshing(true);
      setConnectionError(false);

      const { inicio, fin } = monthToDateRange(mesFilter);

      // Ejecutar todas las consultas en paralelo (resiliente: un fallo no tumba el resto)
      const [
        alertsRes,
        summaryRes,
        byRegionRes,
        byProviderRes,
        multiCloudRes
      ] = await Promise.allSettled([
        alertsApi.getActiveAlerts({ limit: 200 }),
        alertsApi.getAlertsSummary(),
        alertsApi.getAlertsByRegion(),
        alertsApi.getAlertsByProvider(),
        alertsApi.getMultiCloudAlerts({
          fecha_inicio: inicio,
          fecha_fin: fin,
          limit: 1000,
          ...(clienteFilter ? { cliente: clienteFilter } : {})
        })
      ]);

      if (alertsRes.status === 'fulfilled') {
        const alerts = alertsRes.value.data?.alerts || [];
        setProviderAlerts(alerts);
        // Derivar alertsByRegion desde la lista de alertas activas (más fiable
        // que el endpoint /summary/by-region cuando el campo region es NULL)
        const regionCounts = {};
        alerts.forEach((a) => {
          if (a.region) {
            const k = a.region.toLowerCase();
            regionCounts[k] = (regionCounts[k] || 0) + 1;
          }
        });
        setAlertsByRegion((prev) => ({ ...prev, ...regionCounts }));
      }
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data?.data?.summary || summaryRes.value.data?.summary || {});
      }
      if (byRegionRes.status === 'fulfilled') {
        // Combinar con los conteos derivados de providerAlerts (ya seteados arriba)
        const endpointData = byRegionRes.value.data?.data || {};
        setAlertsByRegion((prev) => ({ ...endpointData, ...prev }));
      }
      if (byProviderRes.status === 'fulfilled') {
        setAlertsByProvider(byProviderRes.value.data?.data || {});
      }
      if (multiCloudRes.status === 'fulfilled') {
        const rows = multiCloudRes.value.data?.data || [];
        setClientAlerts(rows);
        // Poblar lista de clientes únicos (para el selector, sin filtro de cliente aplicado)
        if (!clienteFilter) {
          const uniq = [...new Set(rows.map(a => a.cliente).filter(Boolean))].sort();
          setAvailableClientes(uniq);
        }
      }

      const failures = [alertsRes, summaryRes, byRegionRes, byProviderRes, multiCloudRes]
        .filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        failures.forEach(f => console.error('Error fetching alerts data:', f.reason));
      }
      setConnectionError(failures.length === 5);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching alerts data:', error);
      setConnectionError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesFilter, clienteFilter]);

  const handleRegionClick = (region) => {
    setRegionFilter(region);
  };

  // ====== FILTRADO DE ALERTAS DE CLIENTES (client-side) ======
  const availableClouds = [...new Set(clientAlerts.map(a => a.nube || a.provider).filter(Boolean))].sort();
  const availableSeverities = [...new Set(clientAlerts.map(a => a.severity_normalized).filter(Boolean))].sort();

  const filteredClientAlerts = clientAlerts.filter(alert => {
    const cloud = alert.nube || alert.provider || '';
    const matchesCloud = !cloudFilter || cloud === cloudFilter;
    const matchesSeverity = !severityFilter || (alert.severity_normalized || '').toUpperCase() === severityFilter;
    const matchesActive = !onlyActive || alert.is_active;
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term ||
      (alert.service || '').toLowerCase().includes(term) ||
      (alert.resource_name || '').toLowerCase().includes(term) ||
      (alert.project_id || '').toLowerCase().includes(term) ||
      (alert.cliente || '').toLowerCase().includes(term) ||
      (alert.region || '').toLowerCase().includes(term);
    return matchesCloud && matchesSeverity && matchesActive && matchesSearch;
  });

  // ====== KPIS: alertas de proveedor (summary) + alertas de clientes ======
  const severityCounts = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].reduce((acc, sev) => {
    const clientCount = clientAlerts.filter(
      a => (a.severity_normalized || '').toUpperCase() === sev
    ).length;
    const providerCount = Number(summary?.[sev.toLowerCase()] || 0);
    acc[sev] = clientCount + providerCount;
    return acc;
  }, {});
  const totalActiveCount =
    clientAlerts.filter(a => a.is_active).length + Number(summary?.total_active || 0);

  // Alertas de proveedor filtradas desde el mapa o la lista lateral.
  const filteredProviderAlerts = providerAlerts.filter(alert =>
    (!regionFilter || (alert.region || '').toLowerCase().includes(regionFilter.toLowerCase())) &&
    (!providerFilter || alert.provider === providerFilter)
  );

  const handleExportCSV = () => {
    if (!filteredClientAlerts || filteredClientAlerts.length === 0) return;

    const headers = ['Fecha', 'Nube', 'Servicio', 'Recurso', 'Proyecto ID', 'Cliente', 'Región', 'Severidad', 'Estado', 'Activa'];
    const rows = filteredClientAlerts.map(alert => [
      alert.event_ts ? new Date(alert.event_ts).toLocaleString('es-MX') : (alert.fecha || '-'),
      alert.nube || alert.provider || '-',
      alert.service || '-',
      alert.resource_name || '-',
      alert.project_id || '-',
      alert.cliente || '-',
      alert.region || '-',
      alert.severity_normalized || '-',
      alert.status_normalized || '-',
      alert.is_active ? 'Sí' : 'No'
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alertas-clientes-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  // ====== ESTILOS KLOUD OPS ======
  const themeClasses = 'bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.22),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.12),transparent_25%),linear-gradient(135deg,#140728_0%,#0b041a_50%,#1a0b36_100%)] text-white';
  const panelClasses = 'rounded-2xl border border-violet-500/25 bg-[#1a0d36]/70 backdrop-blur-md shadow-[0_18px_60px_rgba(0,0,0,0.24)]';
  const fieldClasses = 'rounded-xl border border-violet-400/30 bg-black/20 px-3 py-2 text-sm text-violet-50 placeholder:text-violet-200/45 focus:border-cyan-400/60 focus:outline-none';
  // Los <select> necesitan fondo sólido (no transparente) para que el dropdown nativo sea legible
  const selectClasses = 'rounded-xl border border-violet-400/30 bg-[#160a2e] px-3 py-2 text-sm text-violet-100 focus:border-cyan-400/60 focus:outline-none cursor-pointer';
  const selectStyle = { colorScheme: 'dark' };

  // ====== RENDER ======

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses} flex items-center justify-center`}>
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-violet-400"></div>
          <span className="text-xl font-semibold">Cargando Centro de Alertas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses} px-4 pb-8 pt-4 font-sans md:px-6 xl:px-8`}>
      {/* ===== HEADER ===== */}
      <div className={`${panelClasses} px-5 py-4 md:px-6`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-200/75">
              Kloud Ops / Centro de Alertas
            </p>
            <h1 className="mt-2 text-3xl font-bold text-violet-100">
              Centro de Alertas
            </h1>
            <p className="mt-1 text-sm text-violet-200/75">
              Salud de proveedores cloud y alertas de servicios de clientes multi-nube.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-xl border border-violet-400/30 bg-black/20 px-3 py-2 text-xs font-medium text-violet-100/85">
              <Clock className="h-3.5 w-3.5" />
              {lastUpdated ? lastUpdated.toLocaleTimeString('es-MX') : 'Sin datos'}
            </span>

            <button
              onClick={fetchAllData}
              disabled={refreshing}
              className="inline-flex items-center justify-center rounded-xl border border-violet-300/40 bg-violet-500/20 p-2 text-violet-50 transition-all hover:bg-violet-500/35 disabled:cursor-not-allowed disabled:opacity-50"
              title="Actualizar datos"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={handleExportCSV}
              disabled={filteredClientAlerts.length === 0}
              className="inline-flex items-center justify-center rounded-xl border border-violet-300/40 bg-violet-500/20 p-2 text-violet-50 transition-all hover:bg-violet-500/35 disabled:cursor-not-allowed disabled:opacity-50"
              title="Exportar alertas de clientes a CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ===== FILTROS GLOBALES ===== */}
        <div className="mt-4 border-t border-violet-500/20 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-violet-200/60">
              <Filter className="h-3.5 w-3.5" />
              Filtros
            </span>

            {/* Selector de mes */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-violet-200/70">Mes:</label>
              <input
                type="month"
                value={mesFilter}
                onChange={(e) => setMesFilter(e.target.value)}
                className={`${selectClasses} text-xs`}
                style={selectStyle}
              />
            </div>

            {/* Selector de cliente */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-violet-200/70">Cliente:</label>
              <select
                value={clienteFilter}
                onChange={(e) => setClienteFilter(e.target.value)}
                className={`${selectClasses} min-w-[200px] text-xs`}
                style={selectStyle}
              >
                <option value="">Todos los clientes</option>
                {availableClientes.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Chip de cliente activo */}
            {clienteFilter && (
              <button
                onClick={() => setClienteFilter('')}
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-400/20"
              >
                {clienteFilter} <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {connectionError && (
        <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-5 py-3 text-sm text-rose-200">
          No se pudo conectar con la API de alertas. Verifica la disponibilidad del backend productivo e intenta actualizar.
        </div>
      )}

      {/* ===== KPIS ===== */}
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className={`${panelClasses} p-4`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-200/70">Total Activas</p>
          <p className="mt-2 text-3xl font-bold text-cyan-300">{totalActiveCount}</p>
        </div>
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map((severity) => (
          <div key={severity} className={`${panelClasses} p-4`}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SEVERITY_COLORS[severity] }} />
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-200/70">{severity}</p>
            </div>
            <p className="mt-2 text-3xl font-bold" style={{ color: SEVERITY_COLORS[severity] }}>
              {severityCounts[severity]}
            </p>
          </div>
        ))}
      </div>

      {/* ===== SECCIÓN 1: SALUD DE PROVEEDORES (MAPA) ===== */}
      <div className={`${panelClasses} mt-6 overflow-hidden`}>
        <div className="border-b border-violet-500/20 px-5 py-4 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="rounded-xl border border-violet-400/30 bg-violet-500/20 p-2 text-cyan-300">
                <Globe2 className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-violet-100">Salud de Proveedores Cloud</h2>
                <p className="text-xs text-violet-200/70">
                  Alertas de health check de AWS, Azure, GCP, Huawei y M365 por región.
                </p>
              </div>
            </div>

            {regionFilter && (
              <button
                onClick={() => setRegionFilter(null)}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-400/20"
              >
                Región: {regionFilter}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 p-4 md:p-5 lg:grid-cols-3">
          {/* Mapa */}
          <div className="lg:col-span-2" style={{ height: '420px' }}>
            <AlertsMap
              alertsByRegion={alertsByRegion}
              providerAlerts={providerAlerts}
              loading={refreshing && providerAlerts.length === 0}
              onRegionClick={handleRegionClick}
              providerFilter={providerFilter}
            />
          </div>

          {/* Panel lateral: proveedores + alertas recientes */}
          <div className="flex flex-col gap-4">
            {/* Distribución por proveedor */}
            <div className="rounded-2xl border border-violet-500/20 bg-black/20 p-4">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200/75">
                Alertas por proveedor
              </p>
              {Object.keys(alertsByProvider).length === 0 ? (
                <p className="text-xs text-violet-200/60">Sin alertas activas de proveedores.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(alertsByProvider).map(([provider, count]) => {
                    const isActive = providerFilter === provider;
                    return (
                      <button
                        key={provider}
                        onClick={() => handleProviderClick(provider)}
                        className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 transition-all ${
                          isActive
                            ? 'border-violet-300/50 bg-violet-500/25'
                            : 'border-violet-500/15 bg-violet-500/10 hover:bg-violet-500/20'
                        }`}
                      >
                        <span className="text-sm font-semibold" style={{ color: PROVIDER_COLORS[provider] || '#c4b5fd' }}>
                          {provider}
                        </span>
                        <span className="rounded-full bg-violet-500/30 px-2.5 py-0.5 text-xs font-bold text-violet-50">
                          {count}
                        </span>
                      </button>
                    );
                  })}
                  {providerFilter && (
                    <button
                      onClick={() => setProviderFilter(null)}
                      className="mt-1 w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 py-1 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-400/20"
                    >
                      Quitar filtro
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Alertas de proveedor recientes */}
            <div className="custom-scrollbar flex-1 overflow-y-auto rounded-2xl border border-violet-500/20 bg-black/20 p-4" style={{ maxHeight: '250px' }}>
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200/75">
                Alertas de proveedor {providerFilter ? `· ${providerFilter}` : regionFilter ? `· ${regionFilter}` : 'recientes'}
              </p>
              {filteredProviderAlerts.length === 0 ? (
                <p className="text-xs text-violet-200/60">
                  {regionFilter ? 'Sin alertas en esta región.' : 'Todos los proveedores operan con normalidad.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredProviderAlerts.slice(0, 20).map((alert) => (
                    <div key={alert.alert_id} className="rounded-xl border border-violet-500/15 bg-violet-500/5 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                          style={{
                            color: SEVERITY_COLORS[alert.severity] || '#a78bfa',
                            background: `${SEVERITY_COLORS[alert.severity] || '#a78bfa'}1f`,
                            border: `1px solid ${SEVERITY_COLORS[alert.severity] || '#a78bfa'}55`
                          }}
                        >
                          {alert.severity}
                        </span>
                        <span className="text-[10px] font-semibold" style={{ color: PROVIDER_COLORS[alert.provider] || '#c4b5fd' }}>
                          {alert.provider}{alert.region ? ` · ${alert.region}` : ''}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs text-violet-100/90" title={alert.summary}>
                        {alert.summary}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECCIÓN 2: ALERTAS DE CLIENTES (TABLA) ===== */}
      <div className={`${panelClasses} mt-6 overflow-hidden`}>
        <div className="border-b border-violet-500/20 px-5 py-4 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="rounded-xl border border-violet-400/30 bg-violet-500/20 p-2 text-cyan-300">
                <Users className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-violet-100">Alertas de Clientes Multi-Nube</h2>
                <p className="text-xs text-violet-200/70">
                  Alertas sobre servicios de clientes en las diferentes nubes · {mesFilter}.
                </p>
              </div>
            </div>

            <span className="rounded-xl border border-violet-400/30 bg-black/20 px-3 py-1.5 text-xs font-medium text-violet-100/85">
              {filteredClientAlerts.length} de {clientAlerts.length} alertas
            </span>
          </div>

          {/* Toolbar de filtros */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-violet-200/50" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar servicio, recurso o región..."
                className={`${fieldClasses} w-64 pl-9`}
              />
            </div>

            <select
              value={cloudFilter}
              onChange={(e) => setCloudFilter(e.target.value)}
              className={selectClasses}
              style={selectStyle}
            >
              <option value="">Todas las nubes</option>
              {availableClouds.map(cloud => (
                <option key={cloud} value={cloud}>{cloud}</option>
              ))}
            </select>

            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className={selectClasses}
              style={selectStyle}
            >
              <option value="">Todas las severidades</option>
              {availableSeverities.map(sev => (
                <option key={sev} value={sev.toUpperCase()}>{sev}</option>
              ))}
            </select>

            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-violet-200/80">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
                className="h-4 w-4 rounded border-violet-400/40 bg-black/20 text-cyan-400 focus:ring-cyan-400"
              />
              Solo activas
            </label>
          </div>
        </div>

        <ClientAlertsTable
          alerts={filteredClientAlerts}
          loading={refreshing && clientAlerts.length === 0}
          maxHeight="520px"
          onRowClick={setSelectedAlert}
        />
      </div>

      {/* ===== MODAL DETALLE ===== */}
      {selectedAlert && (
        <AlertDetailModal alert={selectedAlert} onClose={closeNotificationAlert} />
      )}
      {notificationError && <p role="alert" className="mt-4 rounded-lg border border-red-400/40 bg-red-950 p-3 text-sm text-red-100">{notificationError}</p>}

      {/* Custom Scrollbar CSS */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(139, 92, 246, 0.08);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.35);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.55);
        }
      `}</style>
    </div>
  );
}
